import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMerchantStore } from "../store/useMerchantStore";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getMerchantToken, apiUrl } from "../services/api";
import {
  classifyMerchantRoute,
  summarizeMerchantRoutePipeline,
  removeRouteDispatchMark,
} from "../utils/merchantRouteTracking";
import { EmptyState, FilterBar, PageHeader, SearchField, statusColors } from "../components/orderKit";
import { PipelineTrack, RouteCard, RouteStats, routeMatchesSearch } from "../components/routeKit";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

/** Where a live route is in the delivery pipeline, in plain words (app: _liveLabel). */
const BUCKET_LABEL = {
  awaiting: { text: "live.waiting_for_driver", status: "BROADCASTED" },
  accepted: { text: "route.pipeline.accepted", status: "ASSIGNED" },
  dispatched: { text: "route.pipeline.on_the_road", status: "PICKED_UP" },
  completed: { text: "route.pipeline.completed", status: "COMPLETED" },
};

export default function BroadcastedRoutes() {
  const { merchantId } = useMerchantStore();
  const navigate = useNavigate();

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /** all | awaiting | accepted | dispatched | completed */
  const [routeFilter, setRouteFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [live, setLive] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = getMerchantToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(apiUrl(path), { ...options, headers });
  }, []);

  const fetchData = useCallback(async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");
    try {

      const routesRes = await authFetch(`/api/orders/routes?merchantId=${merchantId}`);
      if (!routesRes.ok) throw new Error(t("routes.load_failed"));
      const routesData = await routesRes.json();
      setRoutes(routesData);
    } catch (err) {
      console.error("[FETCH ERROR]", err);
      setError(friendlyError(err, t("routes.load_failed")));
    } finally {
      setLoading(false);
    }
  }, [merchantId, authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!merchantId) return;

    const socket = new SockJS(apiUrl("/ws"));
    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: (() => {
        const token = getMerchantToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      })(),
      reconnectDelay: 3000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      setLive(true);
      client.subscribe("/topic/merchant/route-claims", (message) => {
          fetchData();
      });
    };

    client.onWebSocketClose = () => setLive(false);
    client.onWebSocketError = (err) => console.error("[WS ERROR]", err);
    client.onStompError = (frame) => console.error("[STOMP ERROR]", frame);

    client.activate();

    return () => {
      if (client) client.deactivate();
    };
  }, [merchantId, fetchData]);

  const pipeline = useMemo(
    () => summarizeMerchantRoutePipeline(routes, merchantId),
    [routes, merchantId]
  );

  const filteredRoutes = useMemo(() => {
    const byBucket =
      routeFilter === "all" ? routes : routes.filter((r) => classifyMerchantRoute(r, merchantId) === routeFilter);
    return byBucket.filter((r) => routeMatchesSearch(r, search)).sort((a, b) => Number(b.routeId) - Number(a.routeId));
  }, [routes, merchantId, routeFilter, search]);

  const filterOptions = [
    { id: "all", label: t("common.all"), count: routes.length },
    { id: "awaiting", label: t("live.waiting_for_driver"), count: pipeline.awaiting, dot: statusColors("BROADCASTED").fg },
    { id: "accepted", label: t("route.pipeline.accepted"), count: pipeline.acceptedPendingDispatch, dot: statusColors("ASSIGNED").fg },
    { id: "dispatched", label: t("route.pipeline.on_the_road"), count: pipeline.dispatchedActive, dot: statusColors("PICKED_UP").fg },
    { id: "completed", label: t("route.pipeline.completed"), count: pipeline.completed, dot: statusColors("COMPLETED").fg },
  ];

  const handleRouteClick = async (route) => {
    try {
      setOpeningId(route.routeId);
      const res = await authFetch(`/api/orders/routes/${route.routeId}/details`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const routeDetails = await res.json();

      const ordersRes = await authFetch(`/api/orders?merchantId=${merchantId}`);
      if (!ordersRes.ok) throw new Error(`HTTP ${ordersRes.status}`);
      const allOrders = await ordersRes.json();

      const routeOrders = allOrders.filter((order) =>
        routeDetails.orderedOrderIds?.includes(order.id)
      );

      navigate(`/broadcasted-route/${route.routeId}`, {
        state: {
          result: routeDetails,
          selectedOrders: routeOrders,
          merchantId,
        },
      });
    } catch (err) {
      console.error("Failed to load route:", err);
      setNotice({ tone: "error", text: t("routes.open_failed") });
      setOpeningId(null);
    }
  };

  const unassignAndRebroadcast = async (routeId) => {
    if (
      !window.confirm(t("live.unassign_confirm", { id: routeId }))
    ) {
      return;
    }
    setUnassigningId(routeId);
    try {
      const res = await authFetch(`/api/orders/routes/${routeId}/unassign-and-broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || t("live.unassign_failed_short"));
      }

      setNotice({ tone: "success", text: t("live.unassigned", { id: routeId }) });
      if (merchantId != null) removeRouteDispatchMark(merchantId, routeId);
      fetchData();
    } catch (err) {
      console.error("Unassign error:", err);
      setNotice({ tone: "error", text: t("live.unassign_failed", { id: routeId, reason: friendlyError(err) }) });
    } finally {
      setUnassigningId(null);
    }
  };

  if (!merchantId) return <div className="card">{t("routes.sign_in")}</div>;

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={t("live.eyebrow")}
        title={t("live.title")}
        lead={t("live.lead")}
        actions={
          <>
            <span className={`live-badge${live ? " live-badge--on" : ""}`} title={live ? t("live.receiving") : t("live.reconnecting")}>
              <span className="live-badge__dot" /> {live ? t("live.live") : t("live.offline")}
            </span>
            <button type="button" onClick={fetchData} disabled={loading}>
              <i className={`fa-solid fa-rotate-right${loading ? " fa-spin" : ""}`} aria-hidden="true" /> {t("common.refresh")}
            </button>
          </>
        }
      />

      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}
      {notice ? (
        <div className={`alert alert--${notice.tone}`} role="status">
          <i className={`fa-solid ${notice.tone === "error" ? "fa-circle-exclamation" : "fa-circle-check"}`} aria-hidden="true" />{" "}
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button type="button" className="alert__close" onClick={() => setNotice(null)} aria-label={t("common.dismiss")}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {loading && routes.length === 0 ? (
        <div className="loading">{t("routes.loading")}</div>
      ) : routes.length === 0 ? (
        <EmptyState
          icon="fa-tower-broadcast"
          title={t("live.empty_title")}
          message={t("live.empty_message")}
        />
      ) : (
        <>
          <RouteStats routes={routes} payLabel={t("route.stats.driver_pay")} />

          <div className="list-toolbar">
            <FilterBar options={filterOptions} value={routeFilter} onChange={setRouteFilter} ariaLabel={t("route.pipeline.aria")} />
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder={t("live.search_placeholder")}
              ariaLabel={t("routes.search_aria")}
            />
          </div>

          {filteredRoutes.length === 0 ? (
            <EmptyState
              icon="fa-tower-broadcast"
              title={t("common.nothing_here")}
              message={t("routes.no_match")}
              action={
                <button
                  type="button"
                  onClick={() => {
                    setRouteFilter("all");
                    setSearch("");
                  }}
                >
                  {t("routes.show_all")}
                </button>
              }
            />
          ) : (
            <div className="route-cards">
              {filteredRoutes.map((route) => {
                const bucket = classifyMerchantRoute(route, merchantId);
                const label = BUCKET_LABEL[bucket];
                const colors = label ? statusColors(label.status) : null;
                const routeStatus = String(route.status || "").toUpperCase();
                const routeLockedForUnassign = routeStatus !== "ASSIGNED" || route.pickedUp === true;
                const phone = route.driverPhone ? String(route.driverPhone).replace(/^\+/, "") : null;

                return (
                  <RouteCard
                    key={route.routeId}
                    route={route}
                    onOpen={handleRouteClick}
                    busy={openingId === route.routeId}
                    tone={bucket === "accepted" || bucket === "dispatched" ? "active" : bucket === "completed" ? "done" : null}
                    statusLabel={label ? { text: t(label.text), bg: colors.bg, fg: colors.fg } : null}
                  >
                    <PipelineTrack bucket={bucket} />
                    {route.driverId ? (
                      <div className="driver-block">
                        <span className="driver-block__avatar">
                          <i className="fa-solid fa-user" aria-hidden="true" />
                        </span>
                        <div className="driver-block__info">
                          <div className="driver-block__name">{t("live.driver_n", { id: route.driverId })}</div>
                          {phone ? (
                            <a className="driver-block__phone" href={`tel:+${phone}`}>
                              <i className="fa-solid fa-phone" aria-hidden="true" /> +{phone}
                            </a>
                          ) : (
                            <span className="muted" style={{ fontSize: 12.5 }}>
                              {t("live.phone_not_shared")}
                            </span>
                          )}
                        </div>
                        {!routeLockedForUnassign && (
                          <button
                            type="button"
                            className="danger"
                            disabled={unassigningId === route.routeId}
                            onClick={() => unassignAndRebroadcast(route.routeId)}
                          >
                            <i className="fa-solid fa-user-xmark" aria-hidden="true" />{" "}
                            {unassigningId === route.routeId ? t("live.unassigning") : t("live.unassign")}
                          </button>
                        )}
                      </div>
                    ) : bucket === "awaiting" ? (
                      <div className="driver-block driver-block--waiting">
                        <span className="driver-block__avatar">
                          <i className="fa-solid fa-hourglass-half" aria-hidden="true" />
                        </span>
                        <span className="muted" style={{ fontSize: 13 }}>
                          {t("live.no_driver_yet")}
                        </span>
                      </div>
                    ) : (
                      <div className="driver-block driver-block--waiting">
                        <span className="driver-block__avatar">
                          <i className="fa-solid fa-user" aria-hidden="true" />
                        </span>
                        <span className="muted" style={{ fontSize: 13 }}>
                          {t("live.no_driver_details")}
                        </span>
                      </div>
                    )}
                  </RouteCard>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
